/// FOURJS_START_COPYRIGHT(D,2018)
/// Property of Four Js*
/// (c) Copyright Four Js 2018, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("FocusApplicationService", ["ApplicationServiceBase", "ApplicationServiceFactory"], function (context, cls) {
  /**
   * @class FocusApplicationService
   * @memberOf classes
   * @extends classes.ApplicationServiceBase
   */
  class FocusApplicationService extends cls.ApplicationServiceBase /** @lends classes.FocusApplicationService.prototype */ {
    constructor(app) {
      super(app);

      /**
       * Active DropDown widget
       * @type {classes.WidgetGroupBase}
       */
      this._activeDropDownWidget = null;

      /**
       * Form node for which the program requested an element to be made visible (visibleId) during
       * VM processing. Consumed by the next restoreVMFocus of that form.
       * @type {classes.NodeBase}
       */
      this._pendingVisibleIdRequestForm = null;
    }

    /**
     * @inheritDoc
     */
    destroy() {
      super.destroy(this);
      this._activeDropDownWidget = null;
    }

    /**
     * Record that the program explicitly requested an element of the given form to be made visible
     * (visibleId). The live visibleId attribute is transient (the VM resets it to -1), so
     * restoreVMFocus relies on this to keep honoring the requested page for that form.
     * @param {classes.NodeBase} formNode - form the request targets
     */
    flagVisibleIdRequest(formNode) {
      this._pendingVisibleIdRequestForm = formNode;
    }

    /**
     * Restores the focus according to the VM focus
     */
    restoreVMFocus() {
      // Consume any pending program visibleId request up-front (valid for one restore cycle) so it
      // can never leak into a later cycle if we bail out below.
      const requestedForm = this._pendingVisibleIdRequestForm;
      this._pendingVisibleIdRequestForm = null;

      const focusedNode = this._application.getFocusedVMNodeAndValue(true);
      if (!focusedNode) {
        return;
      }
      // set current focused node
      context.LogService.focus.log("restoreVMFocus for node : ", focusedNode);

      const focusedController = focusedNode.getController();
      if (!focusedController) {
        return;
      }
      const focusedWidget = focusedController.getWidget() || focusedController.getCurrentInternalWidget();
      const uiWidget = this._application.getUI().getWidget()?._uiWidget || focusedWidget.getUserInterfaceWidget();
      if (!uiWidget) {
        return;
      }

      if (focusedWidget) {
        // set current VM focused widget
        uiWidget.setFocusedWidget(focusedWidget);
      }

      const vmFocusChanged = uiWidget.hasFocusedWidgetChanged();

      // ensure visible for folder pages and splitview
      const parentForm = focusedNode.getAncestor("Form");
      let visibleId = null;
      if (parentForm) {
        visibleId = parentForm.attribute("visibleId");
      }
      // A program visibleId request on this form must win over the focused field's page. The live
      // attribute is transient (the VM resets it to -1), so also honor the flagged request; scoped
      // to the form so a request on another form doesn't suppress this one's focus-follows-page.
      const programVisibleRequest = requestedForm !== null && requestedForm === parentForm;
      if (vmFocusChanged && !programVisibleRequest && (!visibleId || visibleId === -1)) {
        // Be sure that the widget which get the focus will be visible (ex: set the correct current page in a folder)
        if (focusedController.ensureVisible()) {
          this._application.scheduler.layoutCommand();
        }
      }

      // if a system widget is active we should not change the dom focus
      if (gbc.KeyboardService.hasSystemWidget()) {
        return;
      }

      // Don't reclaim focus from a non-VM widget the user is operating (e.g. a keyboard splitter): its own
      // resize triggers a relayout → VM round-trip → this restore. Mouse is immune (drag events ignore focus).
      const browserOwnsFocus = this._browserOwnsDOMFocus(focusedWidget, vmFocusChanged);

      if (focusedNode.isInTable() && !browserOwnsFocus) {
        //We need to set the focus on the Table to be able to show the rowbound
        let tableNode = focusedNode.getAncestor("Table");
        let tableController = tableNode.getController();
        tableController.setFocus();
      }

      if (!browserOwnsFocus) {
        focusedController.setFocus(!vmFocusChanged); // set ui focus on widget element
      }

      if (vmFocusChanged) {
        cls.DropDownWidget.closeAllOnVMFocusChange(focusedWidget);
      }

      this.emit(context.constants.widgetEvents.focusRestored);
    }

    /**
     * Whether the browser/user owns DOM focus so a VM restore must not relocate it (accessibility mode only).
     * True when focus is inside the focused VM widget, or (when the VM didn't change field) on any other
     * focusable widget the user is operating, e.g. a keyboard splitter. False otherwise.
     * @param {classes.WidgetBase} focusedWidget - the VM-focused widget
     * @param {boolean} vmFocusChanged - whether the VM changed the focused field on this response
     * @returns {boolean}
     * @private
     */
    _browserOwnsDOMFocus(focusedWidget, vmFocusChanged) {
      if (focusedWidget?.browserOwnsDOMFocusInA11yMode()) {
        return true;
      }
      // Accessibility mode only: focus on another widget the user operates (e.g. a keyboard splitter,
      // not the VM-focused field) counts as browser-owned when the VM did not change the field.
      if (!gbc.KeyboardService.isAccessibilityModeEnabled()) {
        return false;
      }
      return !vmFocusChanged && Boolean(gbc.WidgetService.getWidgetFromElement(document.activeElement));
    }

    /**
     * Set the widget which has an active DropDown
     * @param {classes.WidgetGroupBase} activeDDWidget - active DropDown widget
     */
    setActiveDropDownWidget(activeDDWidget) {
      this._activeDropDownWidget = activeDDWidget;
    }

    /**
     * Get the widget which has an active DropDown
     * @return {classes.WidgetGroupBase} the active DropDown widget
     */
    getActiveDropDownWidget() {
      return this._activeDropDownWidget;
    }
  }
  cls.FocusApplicationService = FocusApplicationService;
  cls.ApplicationServiceFactory.register("Focus", cls.FocusApplicationService);
});
